import { useState } from 'react';
import { type WhatsappMessage } from '../whatsappWebApi';
import { cn } from '@/shared/utils/utils';
import { FileText, MapPin, Download, X, ExternalLink, AlertCircle, Clock, LayoutTemplate } from 'lucide-react';

interface MessageBubbleProps {
  msg: WhatsappMessage;
}

/** WhatsApp-style ticks: ✓ sent, ✓✓ delivered, blue ✓✓ read. */
function StatusTicks({ status, error }: { status?: string; error?: string }) {
  if (status === 'failed') {
    return (
      <span title={error || 'Not delivered'}>
        <AlertCircle className="h-3.5 w-3.5 text-danger-text" aria-label="Not delivered" />
      </span>
    );
  }
  if (!status) return <Clock className="h-3 w-3 text-[#667781]" aria-label="Sending" />;
  const double = status === 'delivered' || status === 'read';
  const label = status === 'read' ? 'Read' : status === 'delivered' ? 'Delivered' : 'Sent';
  return (
    <svg viewBox="0 0 16 11" width="16" height="11" className={status === 'read' ? 'text-[#53bdeb]' : 'text-[#667781]'} aria-label={label}>
      <title>{label}</title>
      <path fill="currentColor" d="M11.07.65 4.6 7.12 1.93 4.45.88 5.5l3.72 3.72 7.52-7.52z" />
      {double && <path fill="currentColor" d="M15.07.65 8.6 7.12l-.6-.6-1.05 1.05 1.65 1.65 7.52-7.52z" />}
    </svg>
  );
}

function fileBadge(fileName: string) {
  const ext = (fileName.split('.').pop() || '').toUpperCase();
  return ext.length <= 4 ? ext : 'FILE';
}

export function MessageBubble({ msg }: MessageBubbleProps) {
  const [showLightbox, setShowLightbox] = useState(false);
  const isMe = msg.fromMe;
  const time = msg.timestamp
    ? new Date(msg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';
  const mediaUrl = msg.mediaUrl || (msg.hasMedia ? `/api/whatsapp-web/media/${encodeURIComponent(msg.id)}` : null);

  // Sends made while the test redirect is on carry "[TEST — would go to …]".
  let body = msg.body || '';
  let testNote = '';
  const test = /^\[TEST — would go to ([^\]]+)\]\s*/.exec(body);
  if (test) { testNote = test[1]; body = body.slice(test[0].length); }
  // Older template sends were logged as "[Template: name] a | b | c".
  const rawTemplate = /^\[Template:\s*([^\]]+)\]\s*(.*)$/s.exec(body);
  const templateName = msg.templateName || rawTemplate?.[1]?.trim() || '';
  if (rawTemplate) body = rawTemplate[2].split('|').map((s) => s.trim()).filter(Boolean).join(' · ');
  const fileName = msg.fileName || 'Document';

  return (
    <>
      <div className={cn('flex w-full mb-1', isMe ? 'justify-end' : 'justify-start')}>
        <div
          className={cn(
            'relative max-w-[88%] md:max-w-[70%] rounded-[7.5px] px-[9px] pt-[6px] pb-[20px] shadow-[0_1px_0.5px_rgba(11,20,26,.13)] flex flex-col',
            isMe ? 'bg-[#d9fdd3] rounded-tr-none' : 'bg-white rounded-tl-none',
          )}
        >
          {(templateName || testNote) && (
            <div className="mb-1 flex flex-wrap items-center gap-1">
              {templateName && (
                <span className="inline-flex items-center gap-1 rounded bg-black/[0.06] px-1.5 py-0.5 text-[11px] font-medium text-[#54656f]">
                  <LayoutTemplate className="h-3 w-3" aria-hidden /> Template · {templateName}
                </span>
              )}
              {testNote && (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800" title={`Test send — the real recipient would have been ${testNote}`}>
                  Test send
                </span>
              )}
            </div>
          )}

          {isMe && msg.author && (
            <div className="mb-0.5 text-[11.5px] font-medium text-[#1f7aec]">{msg.author}</div>
          )}

          {msg.hasMedia && msg.type === 'image' && mediaUrl && (
            <button type="button" className="mb-1 overflow-hidden rounded-[6px]" onClick={() => setShowLightbox(true)}>
              <img src={mediaUrl} alt={fileName} className="max-h-72 w-auto rounded-[6px] bg-black/5 object-cover hover:opacity-95" loading="lazy" />
            </button>
          )}

          {msg.hasMedia && msg.type === 'document' && mediaUrl && (
            <a
              href={mediaUrl}
              target="_blank"
              rel="noreferrer"
              className="mb-1.5 flex items-center gap-3 rounded-[6px] border border-black/[0.06] bg-black/[0.05] p-2.5 no-underline hover:bg-black/[0.08]"
            >
              <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-[6px] bg-[#ea4335] text-white">
                <FileText className="h-5 w-5" aria-hidden />
                <span className="-mt-0.5 text-[7.5px] font-bold uppercase leading-none">{fileBadge(fileName)}</span>
              </div>
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium text-[#111b21]">{fileName}</span>
              <Download className="h-4 w-4 shrink-0 text-[#54656f]" aria-hidden />
            </a>
          )}

          {msg.type === 'video' && mediaUrl && (
            <video src={mediaUrl} controls className="mb-1 max-h-72 max-w-[300px] rounded bg-black" />
          )}

          {(msg.type === 'audio' || msg.type === 'ptt') && mediaUrl && (
            <audio src={mediaUrl} controls className="mb-1 h-10 w-[250px] max-w-full" />
          )}

          {msg.type === 'location' && msg.location && (
            <a
              href={`https://maps.google.com/?q=${msg.location.lat},${msg.location.lng}`}
              target="_blank"
              rel="noreferrer"
              className="relative mb-1 block h-32 w-full max-w-[300px] rounded bg-[#e9edef]"
            >
              <MapPin className="absolute inset-0 m-auto h-8 w-8 text-danger-text" fill="currentColor" aria-hidden />
              {msg.location.desc && (
                <span className="absolute bottom-0 left-0 right-0 truncate bg-black/50 p-2 text-xs text-white">{msg.location.desc}</span>
              )}
            </a>
          )}

          {body && (
            <div className="whitespace-pre-wrap break-words text-[14.2px] leading-[19px] text-[#111b21]" dir="auto">
              {body}
            </div>
          )}

          {isMe && msg.status === 'failed' && (
            <div className="mt-1 flex items-start gap-1 text-[12px] text-danger-text">
              <AlertCircle className="mt-[1px] h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{msg.error || 'Not delivered'}</span>
            </div>
          )}

          <div className="absolute bottom-[3px] right-[7px] flex items-center gap-1">
            <span className="whitespace-nowrap text-[11px] leading-[15px] text-[#667781]">{time}</span>
            {isMe && <StatusTicks status={msg.status} error={msg.error} />}
          </div>
        </div>
      </div>

      {showLightbox && mediaUrl && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/85 p-4" onClick={() => setShowLightbox(false)}>
          <div className="absolute right-4 top-4 flex items-center gap-3">
            <a href={mediaUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30" title="Open original">
              <ExternalLink className="h-5 w-5" />
            </a>
            <button type="button" onClick={() => setShowLightbox(false)} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30" title="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <img src={mediaUrl} alt={fileName} className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}
